<template>
  <div class="app-container">
    <Sidebar />
    <div class="edit-sleep-view">
      <div class="edit-container">
        <header class="edit-header">
          <h1 style="color: black;">Edit Sleep Content</h1>
          <router-link to="/sleep" class="back-link">
            <i class="icon-arrow-left"></i> Back to Sleep Items
          </router-link>
        </header>

        <div v-if="loading" class="status-message loading">
          <div class="spinner"></div>
          <span>Loading sleep content...</span>
        </div>
        
        <div v-else-if="error" class="status-message error">
          <i class="icon-error"></i>
          <span>{{ error }}</span>
        </div>
        
        <form v-else @submit.prevent="handleSubmit" class="edit-form">
          <!-- Type selection -->
          <div class="form-group">
            <label for="content-type">Content Type</label>
            <select id="content-type" v-model="formData.type" required>
              <option value="" disabled>Select content type</option>
              <option value="bedtime">Bedtime Story</option>
              <option value="breathing">Breathing Exercise</option>
              <option value="sleep_frequency">Sleep Frequency</option>
              <option value="sounds_of_nature">Sounds of Nature</option>
              <option value="brain_programming">Brain Programming</option>
            </select>
          </div>
          
          <!-- Display Type -->
          <div class="form-group">
            <label for="display-type">Display Type</label>
            <select id="display-type" v-model="formData.display_type" required>
              <option value="still">Still</option>
              <option value="video">Video</option>
            </select>
          </div>
          
          <!-- Title -->
          <div class="form-group">
            <label for="content-title">Title (English)</label>
            <input id="content-title" type="text" v-model="formData.title" required placeholder="Enter content title">
          </div>
          
          <!-- Arabic Title -->
          <div class="form-group">
            <label for="content-title-ar">Title (Arabic)</label>
            <input id="content-title-ar" type="text" v-model="formData.title_ar" dir="rtl" placeholder="أدخل عنوان المحتوى">
          </div>
          
          <!-- File upload -->
          <div v-if="['breathing', 'bedtime', 'sounds_of_nature'].includes(formData.type)" class="form-group">
            <label>Audio File</label>
            <div class="file-upload-container">
              <input type="file" ref="fileInput" @change="handleFileChange" accept="audio/*" class="file-input">
              <button type="button" class="file-select-button" @click="$refs.fileInput.click()">
                <i class="icon-upload"></i> Select Audio File
              </button>
              <span v-if="selectedFile" class="selected-file">{{ selectedFile.name }}</span>
              <span v-else-if="formData.file_url" class="selected-file">
                Current file: {{ getFileNameFromUrl(formData.file_url) }}
              </span>
              <div v-if="uploadStatus" class="upload-status" :class="{ 'uploading': isUploading }">
                <div class="upload-progress" :style="{ width: uploadProgress + '%' }"></div>
                <span>{{ uploadStatus }}</span>
              </div>
            </div>
          </div>
          
          <!-- Image upload for thumbnail -->
          <div class="form-group">
            <label>Thumbnail Image</label>
            <div class="file-upload-container">
              <input type="file" ref="imageInput" @change="handleImageChange" accept="image/*" class="file-input">
              <button type="button" class="file-select-button" @click="$refs.imageInput.click()">
                <i class="icon-image"></i> Select Thumbnail
              </button>
              <span v-if="selectedImage" class="selected-file">{{ selectedImage.name }}</span>
              <span v-else-if="formData.image_url" class="selected-file">
                Current image: {{ getFileNameFromUrl(formData.image_url) }}
              </span>
              <div v-if="imageUploadStatus" class="upload-status" :class="{ 'uploading': isImageUploading }">
                <div class="upload-progress" :style="{ width: imageUploadProgress + '%' }"></div>
                <span>{{ imageUploadStatus }}</span>
              </div>
            </div>
          </div>
          
          <!-- Frequency selection for Sleep Frequency -->
          <div v-if="formData.type === 'sleep_frequency'" class="form-group">
            <label for="frequency">Frequency (Hz)</label>
            <input id="frequency" type="number" v-model.number="formData.frequency" step="0.1" min="0.1" max="40" required>
            <small>Enter a value between 0.1 and 40 Hz</small>
          </div>
          
          <!-- Details/Description -->
          <div class="form-group">
            <label for="details">Description (English)</label>
            <QuillEditor
              id="details"
              v-model:content="formData.details"
              :placeholder="'Enter description in English'"
            />
          </div>
          
          <!-- Arabic Description -->
          <div class="form-group">
            <label for="details-ar">Description (Arabic)</label>
            <QuillEditor
              id="details-ar"
              v-model:content="formData.details_ar"
              :placeholder="'Enter description in Arabic'"
              dir="rtl"
            />
          </div>

          <!-- Status toggle -->
          <div class="form-group status-toggle">
            <label>Status</label>
            <div class="toggle-container">
              <label class="toggle">
                <input type="checkbox" v-model="formData.status">
                <span class="slider"></span>
              </label>
              <span class="status-label">{{ formData.status ? 'Active' : 'Inactive' }}</span>
            </div>
          </div>
          
          <!-- Error display -->
          <div v-if="saveError" class="form-error">
            <i class="icon-alert"></i>
            {{ saveError }}
          </div>
          
          <!-- Additional Media Files -->
          <div v-if="formData.type !== 'sleep_frequency'" class="form-group">
            <!-- <h3 class="section-title" style="color: white;">Additional Media Files</h3> -->
            
            <!-- Arabic Audio File -->
            <div class="form-group">
              <label>Arabic Audio File</label>
              <div class="file-upload-container">
                <input type="file" ref="fileInputAr" @change="handleArFileChange" accept="audio/*" class="file-input">
                <button type="button" class="file-select-button" @click="$refs.fileInputAr.click()">
                  <i class="icon-upload"></i> Select Arabic Audio File
                </button>
                <span v-if="selectedArFile" class="selected-file">{{ selectedArFile.name }}</span>
                <span v-else-if="formData.audio_url_ar" class="selected-file">
                  Current file: {{ getFileNameFromUrl(formData.audio_url_ar) }}
                </span>
              </div>
            </div>
            
            <!-- Voice Over Files -->
            <div class="form-group">
              <label>Voice Over (English)</label>
              <div class="file-upload-container">
                <input type="file" ref="voiceOverInput" @change="handleVoiceOverChange" accept="audio/*" class="file-input">
                <button type="button" class="file-select-button" @click="$refs.voiceOverInput.click()">
                  <i class="icon-upload"></i> Select Voice Over File
                </button>
                <span v-if="selectedVoiceOver" class="selected-file">{{ selectedVoiceOver.name }}</span>
                <span v-else-if="formData.voice_over" class="selected-file">
                  Current file: {{ getFileNameFromUrl(formData.voice_over) }}
                </span>
              </div>
            </div>
            
            <div class="form-group">
              <label>Voice Over (Arabic)</label>
              <div class="file-upload-container">
                <input type="file" ref="voiceOverArInput" @change="handleVoiceOverArChange" accept="audio/*" class="file-input">
                <button type="button" class="file-select-button" @click="$refs.voiceOverArInput.click()">
                  <i class="icon-upload"></i> Select Arabic Voice Over File
                </button>
                <span v-if="selectedVoiceOverAr" class="selected-file">{{ selectedVoiceOverAr.name }}</span>
                <span v-else-if="formData.voice_over_ar" class="selected-file">
                  Current file: {{ getFileNameFromUrl(formData.voice_over_ar) }}
                </span>
              </div>
            </div>
            
            <!-- Video Files (if display type is video) -->
            <div v-if="formData.display_type === 'video'" class="form-group">
              <label>Video File (English)</label>
              <div class="file-upload-container">
                <input type="file" ref="videoInput" @change="handleVideoChange" accept="video/*" class="file-input">
                <button type="button" class="file-select-button" @click="$refs.videoInput.click()">
                  <i class="icon-upload"></i> Select Video File
                </button>
                <span v-if="selectedVideo" class="selected-file">{{ selectedVideo.name }}</span>
                <span v-else-if="formData.video_over" class="selected-file">
                  Current file: {{ getFileNameFromUrl(formData.video_over) }}
                </span>
              </div>
            </div>
            
            <div v-if="formData.display_type === 'video'" class="form-group">
              <label>Video File (Arabic)</label>
              <div class="file-upload-container">
                <input type="file" ref="videoArInput" @change="handleVideoArChange" accept="video/*" class="file-input">
                <button type="button" class="file-select-button" @click="$refs.videoArInput.click()">
                  <i class="icon-upload"></i> Select Arabic Video File
                </button>
                <span v-if="selectedVideoAr" class="selected-file">{{ selectedVideoAr.name }}</span>
                <span v-else-if="formData.video_over_ar" class="selected-file">
                  Current file: {{ getFileNameFromUrl(formData.video_over_ar) }}
                </span>
              </div>
            </div>
          </div>
          
          <!-- Cover Photo (Arabic) -->
          <div class="form-group">
            <label>Arabic Thumbnail Image</label>
            <div class="file-upload-container">
              <input type="file" ref="imageArInput" @change="handleImageArChange" accept="image/*" class="file-input">
              <button type="button" class="file-select-button" @click="$refs.imageArInput.click()">
                <i class="icon-image"></i> Select Arabic Thumbnail
              </button>
              <span v-if="selectedImageAr" class="selected-file">{{ selectedImageAr.name }}</span>
              <span v-else-if="formData.cover_photo_ar" class="selected-file">
                Current image: {{ getFileNameFromUrl(formData.cover_photo_ar) }}
              </span>
            </div>
          </div>
          
          <!-- Duration in seconds -->
          <div class="form-group">
            <label for="duration">Duration (seconds)</label>
            <input id="duration" type="number" v-model.number="formData.duration" min="1" required>
            <small>Enter the duration in seconds (default: 1)</small>
          </div>
          
          <!-- Error display -->
          <div v-if="saveError" class="form-error">
            <i class="icon-alert"></i>
            {{ saveError }}
          </div>
          
          <!-- Form actions -->
          <div class="form-actions">
            <router-link to="/sleep" class="btn-secondary">Cancel</router-link>
            <button type="submit" class="btn-primary" :disabled="isSubmitting">
              <span v-if="isSubmitting" class="spinner small"></span>
              Update Content
            </button>
          </div>
        </form>
      </div>
      
      <!-- Toast Notification -->
      <ToastNotification 
        :show="toast.show"
        :message="toast.message"
        :type="toast.type"
      />
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ToastNotification } from '@/components/sleep';
import authService from '@/utils/auth';
import Sidebar from '@/components/Sidebar.vue';
import QuillEditor from '@/components/QuillEditor.vue';

const API_CONFIG = {
  baseURL: 'https://api.kewaar.com/api/admin',
  apiUrl: 'https://r2-eu1.kewaarcdn.com/api/files',
  imageApiUrl: 'https://imgsuploader.kewaarcdn.com/api/images/upload',
  imageApiKey: 'cM4dZyrrVbI9iW5SgGpzl8lXvn1i340RlsxnEgjofGa0pWjhcDTZWlJOCHVFQaqvIhHrfpzAV46EZ3q7o6VSB2FVsxkh926lj9rFg2cq3a7HbGd98U3a5tWb3vbWnyfk',
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Locale': 'en'
  }
};

export default {
  name: 'EditSleepItemView',
  components: {
    ToastNotification,
    Sidebar,
    QuillEditor
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const itemId = route.params.id;
    
    const loading = ref(true);
    const error = ref(null);
    const saveError = ref(null);
    const isSubmitting = ref(false);
    
    // Form data
    const formData = reactive({
      type: '',
      title: '',
      title_ar: '',
      details: '',
      details_ar: '',
      audio_url: null,
      audio_url_ar: null,
      voice_over: null,
      voice_over_ar: null,
      video_over: null,
      video_over_ar: null,
      cover_photo: null,
      cover_photo_ar: null,
      main_photo: null,
      file_id: null,
      file_url: null,
      image_id: null,
      image_url: null,
      frequency: 4.0,
      duration: 1,
      status: true,
      display_type: 'still'
    });
    
    // File upload states
    const selectedFile = ref(null);
    const uploadStatus = ref('');
    const uploadProgress = ref(0);
    const isUploading = ref(false);
    
    // Image upload states
    const selectedImage = ref(null);
    const imageUploadStatus = ref('');
    const imageUploadProgress = ref(0);
    const isImageUploading = ref(false);
    
    // Additional file upload states
    const selectedArFile = ref(null);
    const selectedVoiceOver = ref(null);
    const selectedVoiceOverAr = ref(null);
    const selectedVideo = ref(null);
    const selectedVideoAr = ref(null);
    const selectedImageAr = ref(null);
    
    // Toast notification state
    const toast = ref({
      show: false,
      message: '',
      type: 'success'
    });
    
    // Check if token is valid
    const checkToken = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        error.value = 'Authentication token is missing. Please log in again.';
        showToast('Please log in again', 'error');
        setTimeout(() => {
          router.push('/login');
        }, 2000);
        return false;
      }
      
      // Check if token is expired
      const expiryTime = localStorage.getItem('token_expires_at');
      if (expiryTime) {
        const expiry = new Date(expiryTime).getTime();
        const now = new Date().getTime();
        const fiveMinutesInMs = 5 * 60 * 1000;
        
        if ((expiry - now) < fiveMinutesInMs) {
          const refreshed = await authService.refreshToken();
          if (!refreshed) {
            error.value = 'Session expired. Please log in again.';
            showToast('Session expired. Please log in again.', 'error');
            setTimeout(() => {
              router.push('/login');
            }, 2000);
            return false;
          }
        }
      }
      
      return true;
    };
    
    // Fetch sleep item data
    const fetchItemData = async () => {
      const tokenValid = await checkToken();
      if (!tokenValid) return;
      
      try {
        loading.value = true;
        error.value = null;
        
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_CONFIG.baseURL}/sleep1/${itemId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        });
        
        if (!response.ok) {
          if (response.status === 401) {
            const refreshed = await authService.refreshToken();
            if (refreshed) {
              return await fetchItemData();
            }
          } else if (response.status === 404) {
            throw new Error('Sleep content not found');
          }
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();
        const item = data.data || {};
        
        // Populate form data
        Object.keys(formData).forEach(key => {
          if (item[key] !== undefined) {
            formData[key] = item[key];
          }
        });
        
        // Set file URLs if available
        if (item.file) {
          formData.file_url = item.file;
        }
        
        if (item.image) {
          formData.image_url = item.image;
        }
        
      } catch (err) {
        console.error('Fetch error:', err);
        error.value = err.message || 'Failed to load sleep content';
      } finally {
        loading.value = false;
      }
    };
    
    // Extract filename from URL
    const getFileNameFromUrl = (url) => {
      if (!url) return '';
      try {
        const urlObj = new URL(url);
        const pathParts = urlObj.pathname.split('/');
        return pathParts[pathParts.length - 1];
      } catch (e) {
        return url.split('/').pop() || url;
      }
    };
    
    // File change handlers
    const handleFileChange = (event) => {
      const file = event.target.files[0];
      if (file) {
        selectedFile.value = file;
        uploadFile(file, false);
      }
    };
    
    const handleImageChange = (event) => {
      const file = event.target.files[0];
      if (file) {
        selectedImage.value = file;
        uploadFile(file, true);
      }
    };
    
    // Additional file change handlers
    const handleArFileChange = (event) => {
      const file = event.target.files[0];
      if (file) {
        selectedArFile.value = file;
        uploadFile(file, 'audio_url_ar');
      }
    };
    
    const handleVoiceOverChange = (event) => {
      const file = event.target.files[0];
      if (file) {
        selectedVoiceOver.value = file;
        uploadFile(file, 'voice_over');
      }
    };
    
    const handleVoiceOverArChange = (event) => {
      const file = event.target.files[0];
      if (file) {
        selectedVoiceOverAr.value = file;
        uploadFile(file, 'voice_over_ar');
      }
    };
    
    const handleVideoChange = (event) => {
      const file = event.target.files[0];
      if (file) {
        selectedVideo.value = file;
        uploadFile(file, 'video_over');
      }
    };
    
    const handleVideoArChange = (event) => {
      const file = event.target.files[0];
      if (file) {
        selectedVideoAr.value = file;
        uploadFile(file, 'video_over_ar');
      }
    };
    
    const handleImageArChange = (event) => {
      const file = event.target.files[0];
      if (file) {
        selectedImageAr.value = file;
        uploadFile(file, 'cover_photo_ar');
      }
    };
    
    // Upload file to server
    const uploadFile = async (file, fieldType) => {
      const tokenValid = await checkToken();
      if (!tokenValid) return;
      
      const isImage = fieldType === true || ['image_id', 'cover_photo_ar'].includes(fieldType);
      
      if (isImage) {
        imageUploadStatus.value = 'Uploading...';
        isImageUploading.value = true;
        imageUploadProgress.value = 0;
        
        // Use the new image upload API for images
        uploadImageFile(file, fieldType);
        return;
      } else {
        uploadStatus.value = 'Uploading...';
        isUploading.value = true;
        uploadProgress.value = 0;
      }
      
      try {
        const token = localStorage.getItem('token');
        const formDataObj = new FormData();
        formDataObj.append('file', file);
        
        // Use XMLHttpRequest for progress tracking
        const xhr = new XMLHttpRequest();
        xhr.open('POST', API_CONFIG.apiUrl, true);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const progress = Math.round((event.loaded / event.total) * 100);
            uploadProgress.value = progress;
          }
        };
        
        xhr.onload = function() {
          if (this.status >= 200 && this.status < 300) {
            try {
              const response = JSON.parse(this.responseText);
              if (response.data && response.data.id) {
                if (fieldType === 'file_id') {
                  formData.file_id = response.data.id;
                  formData.audio_url = response.data.url;
                  uploadStatus.value = 'Upload complete';
                  isUploading.value = false;
                } else {
                  // Handle other field types
                  formData[fieldType] = response.data.url;
                  uploadStatus.value = 'Upload complete';
                  isUploading.value = false;
                }
              } else {
                throw new Error('Invalid response from server');
              }
            } catch (e) {
              throw new Error(`Error parsing response: ${e.message}`);
            }
          } else {
            throw new Error(`Upload failed: ${this.status}`);
          }
        };
        
        xhr.onerror = function() {
          throw new Error('Network error during upload');
        };
        
        xhr.send(formDataObj);
      } catch (err) {
        uploadStatus.value = `Error: ${err.message}`;
        isUploading.value = false;
        saveError.value = `File upload failed: ${err.message}`;
      }
    };
    
    // New method for uploading images using the dedicated image API
    const uploadImageFile = async (file, fieldType) => {
      try {
        const formDataObj = new FormData();
        formDataObj.append('image', file);
        
        // Use XMLHttpRequest for progress tracking
        const xhr = new XMLHttpRequest();
        xhr.open('POST', API_CONFIG.imageApiUrl, true);
        xhr.setRequestHeader('x-api-key', API_CONFIG.imageApiKey);
        
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const progress = Math.round((event.loaded / event.total) * 100);
            imageUploadProgress.value = progress;
          }
        };
        
        xhr.onload = function() {
          if (this.status >= 200 && this.status < 300) {
            try {
              const response = JSON.parse(this.responseText);
              if (response.data && response.data.url) {
                if (fieldType === true || fieldType === 'image_id') {
                  formData.image_id = response.data.id || '';
                  formData.image_url = response.data.url;
                } else if (fieldType === 'cover_photo_ar') {
                  formData.cover_photo_ar = response.data.url;
                }
                imageUploadStatus.value = 'Upload complete';
                isImageUploading.value = false;
              } else {
                throw new Error('Invalid response from server');
              }
            } catch (e) {
              throw new Error(`Error parsing response: ${e.message}`);
            }
          } else {
            throw new Error(`Upload failed: ${this.status}`);
          }
        };
        
        xhr.onerror = function() {
          throw new Error('Network error during upload');
        };
        
        xhr.send(formDataObj);
      } catch (err) {
        imageUploadStatus.value = `Error: ${err.message}`;
        isImageUploading.value = false;
        saveError.value = `Image upload failed: ${err.message}`;
      }
    };
    
    // Form submission
    const handleSubmit = async () => {
      const tokenValid = await checkToken();
      if (!tokenValid) return;
      
      try {
        isSubmitting.value = true;
        saveError.value = null;
        
        // Validate required fields
        if (!formData.title) {
          throw new Error('Title is required');
        }
        
        if (!formData.type) {
          throw new Error('Content type is required');
        }
        
        // Validate audio file for certain types
        if (['breathing', 'bedtime', 'sounds_of_nature'].includes(formData.type) && 
            !formData.file_id && !formData.file_url) {
          throw new Error('Audio file is required for this content type');
        }
        
        // Prepare submission data
        const submissionData = { ...formData };
        
        // Always ensure duration is 1
        submissionData.duration = 1;
        
        // Remove URLs from submission
        delete submissionData.file_url;
        delete submissionData.image_url;
        
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_CONFIG.baseURL}/sleep1/${itemId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(submissionData)
        });
        
        if (!response.ok) {
          if (response.status === 401) {
            const refreshed = await authService.refreshToken();
            if (refreshed) {
              return handleSubmit();
            }
          }
          
          const errorData = await response.json();
          if (errorData.message) {
            throw new Error(errorData.message);
          } else if (errorData.errors && errorData.errors.length > 0) {
            throw new Error(errorData.errors.join(', '));
          }
          
          throw new Error(`HTTP Error: ${response.status}`);
        }
        
        // Success
        showToast('Sleep content updated successfully', 'success');
        setTimeout(() => {
          router.push('/sleep');
        }, 1500);
        
      } catch (err) {
        console.error('Update error:', err);
        saveError.value = err.message || 'Failed to update sleep content';
        showToast('Update failed', 'error');
      } finally {
        isSubmitting.value = false;
      }
    };
    
    // Toast notification
    const showToast = (message, type = 'success') => {
      toast.value.show = true;
      toast.value.message = message;
      toast.value.type = type;
      
      setTimeout(() => {
        toast.value.show = false;
      }, 3000);
    };
    
    // Fetch data on component mount
    onMounted(() => {
      fetchItemData();
    });
    
    return {
      loading,
      error,
      saveError,
      isSubmitting,
      formData,
      selectedFile,
      selectedImage,
      uploadStatus,
      uploadProgress,
      isUploading,
      imageUploadStatus,
      imageUploadProgress,
      isImageUploading,
      toast,
      handleFileChange,
      handleImageChange,
      handleSubmit,
      getFileNameFromUrl,
      selectedArFile,
      selectedVoiceOver,
      selectedVoiceOverAr,
      selectedVideo,
      selectedVideoAr,
      selectedImageAr,
      handleArFileChange,
      handleVoiceOverChange,
      handleVoiceOverArChange,
      handleVideoChange,
      handleVideoArChange,
      handleImageArChange
    };
  }
};
</script>

<style scoped>
.app-container {
  display: flex;
  min-height: 100vh;
}

.edit-sleep-view {
  padding: 2rem;
  max-width: 1000px;
  margin: 0 auto;
  margin-left: 300px;
  width: 100%;
}

.edit-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
}

.edit-header h1 {
  font-size: 1.75rem;
  font-weight: 600;
  margin: 0;
  color: var(--heading-color, #111827);
}

.back-link {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--primary-color, #4f46e5);
  text-decoration: none;
  font-weight: 500;
}

.back-link:hover {
  text-decoration: underline;
}

.status-message {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
}

.status-message.loading {
  background-color: rgba(79, 70, 229, 0.1);
  color: var(--primary-color, #4f46e5);
}

.status-message.error {
  background-color: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.spinner {
  border: 2px solid rgba(79, 70, 229, 0.3);
  border-radius: 50%;
  border-top: 2px solid var(--primary-color, #4f46e5);
  width: 20px;
  height: 20px;
  animation: spin 1s linear infinite;
}

.spinner.small {
  width: 14px;
  height: 14px;
  border-width: 2px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.edit-form {
  background: #1f2937;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.15), 0 2px 4px -1px rgba(0, 0, 0, 0.1);
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: var(--label-color, #374151);
}

.form-group input[type="text"],
.form-group input[type="number"],
.form-group select,
.form-group textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color, #d1d5db);
  border-radius: 8px;
  font-size: 1rem;
  transition: border 0.2s ease;
  background-color: var(--input-bg, #fff);
  color: var(--text-color, #111827);
}

.form-group input[type="text"]:focus,
.form-group input[type="number"]:focus,
.form-group select:focus,
.form-group textarea:focus {
  border-color: var(--primary-color, #4f46e5);
  outline: none;
  box-shadow: 0 0 0 2px rgba(79, 70, 229, 0.1);
}

.form-group small {
  display: block;
  margin-top: 0.5rem;
  font-size: 0.85rem;
  color: var(--text-secondary, #6b7280);
}

.file-input {
  display: none;
}

.file-upload-container {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.file-select-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border: 1px dashed var(--border-color, #d1d5db);
  border-radius: 8px;
  background-color: var(--input-bg, #fff);
  color: var(--text-secondary, #6b7280);
  cursor: pointer;
  font-size: 0.95rem;
  transition: all 0.2s ease;
  max-width: fit-content;
}

.file-select-button:hover {
  border-color: var(--primary-color, #4f46e5);
  color: var(--primary-color, #4f46e5);
}

.selected-file {
  font-size: 0.9rem;
  color: var(--text-secondary, #6b7280);
  margin-top: 0.25rem;
}

.upload-status {
  margin-top: 0.5rem;
  font-size: 0.85rem;
  color: var(--text-color, #111827);
  position: relative;
  height: 22px;
  display: flex;
  align-items: center;
  padding-left: 0.25rem;
  border-radius: 4px;
  overflow: hidden;
}

.upload-status.uploading {
  background-color: rgba(79, 70, 229, 0.05);
}

.upload-progress {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background-color: rgba(79, 70, 229, 0.15);
  z-index: 1;
  transition: width 0.3s ease;
}

.upload-status span {
  position: relative;
  z-index: 2;
}

.status-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.status-toggle label {
  margin-bottom: 0;
}

.toggle-container {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.toggle {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 26px;
}

.toggle input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--toggle-bg, #e5e7eb);
  transition: .4s;
  border-radius: 34px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  transition: .4s;
  border-radius: 50%;
}

input:checked + .slider {
  background-color: var(--primary-color, #4f46e5);
}

input:focus + .slider {
  box-shadow: 0 0 1px var(--primary-color, #4f46e5);
}

input:checked + .slider:before {
  transform: translateX(24px);
}

.status-label {
  font-size: 0.9rem;
  color: var(--text-secondary, #6b7280);
}

.form-error {
  background-color: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  padding: 0.75rem;
  border-radius: 8px;
  font-size: 0.9rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.form-actions {
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  justify-content: flex-end;
}

.btn-primary,
.btn-secondary {
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  min-width: 120px;
  text-decoration: none;
  text-align: center;
}

.btn-primary {
  background-color: var(--primary-color, #4f46e5);
  color: white;
  border: none;
}

.btn-primary:hover:not(:disabled) {
  background-color: #4338ca;
}

.btn-primary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-secondary {
  background-color: transparent;
  border: 1px solid var(--border-color, #d1d5db);
  color: var(--text-color, #111827);
}

.btn-secondary:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

/* Dark theme overrides */
:root[data-theme="dark"] .edit-form {
  background-color: #1f2937;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px -1px rgba(0, 0, 0, 0.1);
}

:root[data-theme="dark"] .edit-header h1 {
  color: #f9fafb;
}

:root[data-theme="dark"] .form-group label {
  color: #e5e7eb;
}

:root[data-theme="dark"] .form-group input[type="text"],
:root[data-theme="dark"] .form-group input[type="number"],
:root[data-theme="dark"] .form-group select,
:root[data-theme="dark"] .form-group textarea {
  background-color: #374151;
  color: #f9fafb;
  border-color: #4b5563;
}

:root[data-theme="dark"] .file-select-button {
  background-color: #374151;
  color: #d1d5db;
  border-color: #4b5563;
}

:root[data-theme="dark"] .form-group small {
  color: #d1d5db;
}

:root[data-theme="dark"] .btn-secondary {
  background-color: transparent;
  border-color: #4b5563;
  color: #e5e7eb;
}

/* Mobile styles */
@media (max-width: 768px) {
  .edit-sleep-view {
    margin-left: 0;
  }
}

/* Editor styles are handled by QuillEditor component */
</style> 